import { useCallback, useEffect, useState } from 'react';
import { projectId } from '../utils/supabase/info';
import { listAnswersToCheck, type AnswerToCheck } from '../utils/discoveryGridApi';
import { usePendingDecisions } from './usePendingDecisions';
import { useReviewQueueUnified } from './useReviewQueueUnified';
import { useSpaceReviews } from './useSpaceReviews';
import { useTodos, type UseTodosResult } from './useTodos';
import { queueHeadline, queueSections, queueTotal, reviewItemNeedsAttention, type QueueSection, type QueueSectionId } from '../utils/queueCounts';

/**
 * useQueue — every source of "what needs me" for a consultant, composed
 * once, with one total. The dashboard's Queue tab, its badge and its
 * "Needs Your Action" card all read this hook, so they can never disagree
 * (audit finding C7; ledger L22).
 *
 * Sources: answers to check (level-3 grid cells), client submissions (space
 * planning reviews), items for review (the unified review queue), action
 * items (passed in, the dashboard already loads them), pending decisions,
 * escalation requests in the consultant's inbox, and the person's own
 * to-dos (which used to live in a dropdown in the top bar).
 */

export interface ActionItemsLike {
  totalCount: number;
  refresh?: () => void;
}

export interface UseQueueInput {
  accessToken: string;
  currentUser: any;
  actionItems: ActionItemsLike;
}

export interface UseQueueResult {
  counts: Record<QueueSectionId, number>;
  sections: QueueSection[];
  total: number;
  headline: string;
  loading: boolean;
  answers: { items: AnswerToCheck[]; total: number };
  todos: UseTodosResult;
  refresh: () => void;
}

const ANSWERS_PREVIEW = 8;

export function useQueue({ accessToken, currentUser, actionItems }: UseQueueInput): UseQueueResult {
  const { pendingCount: decisions, refetch: refetchDecisions } = usePendingDecisions(currentUser?.id, accessToken, !!currentUser?.id);
  const review = useReviewQueueUnified({ accessToken, currentUser });
  const submissions = useSpaceReviews(accessToken);
  const todos = useTodos(currentUser?.id, accessToken, !!currentUser?.id);

  const [answers, setAnswers] = useState<{ items: AnswerToCheck[]; total: number }>({ items: [], total: 0 });
  const [answersLoading, setAnswersLoading] = useState(true);
  const [escalations, setEscalations] = useState(0);
  const [escalationsLoading, setEscalationsLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setAnswersLoading(true);
    listAnswersToCheck(accessToken, ANSWERS_PREVIEW)
      .then((r) => { if (!cancelled) setAnswers({ items: r.items ?? [], total: r.total ?? 0 }); })
      .catch((e) => console.error('[useQueue] answers to check failed:', e))
      .finally(() => { if (!cancelled) setAnswersLoading(false); });
    return () => { cancelled = true; };
  }, [accessToken, tick]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setEscalationsLoading(true);
    fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/escalation/inbox`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(async (r) => (r.ok ? r.json() : { inbox: [] }))
      .then((data) => {
        if (cancelled) return;
        const list: any[] = data?.inbox ?? data?.requests ?? [];
        setEscalations(list.filter((item) => item && item.status !== 'resolved' && item.status !== 'closed').length);
      })
      .catch((e) => console.error('[useQueue] escalation inbox failed:', e))
      .finally(() => { if (!cancelled) setEscalationsLoading(false); });
    return () => { cancelled = true; };
  }, [accessToken, tick]);

  const counts: Record<QueueSectionId, number> = {
    answers: answers.total,
    submissions: submissions.count,
    review: (review.items ?? []).filter(reviewItemNeedsAttention).length,
    actions: actionItems?.totalCount ?? 0,
    decisions: decisions ?? 0,
    escalations,
    todos: todos.pending.length,
  };
  const sections = queueSections(counts);
  const total = queueTotal(sections);

  const refresh = useCallback(() => {
    setTick((n) => n + 1);
    review.refresh?.();
    refetchDecisions?.();
    actionItems?.refresh?.();
    void todos.reload();
  }, [review, refetchDecisions, actionItems, todos]);

  return {
    counts,
    sections,
    total,
    headline: queueHeadline(total),
    loading: answersLoading || escalationsLoading || review.loading || submissions.loading || todos.loading,
    answers,
    todos,
    refresh,
  };
}
